/** Bulk Set contractor/crew on outline Service Lines — dialog once, patch each line, in-place row update. */
import { Entity, outlineCrewEntities } from '@jms/model';
import { EntityDataService } from '@workbuddy/services/entities';
import { ToastService } from '@workbuddy/ui/services';
import { SemanticCommand } from '@workbuddy/ui/services/keyboard.shortcuts';
import { WorkBuddyDialogService } from '@workbuddy/ui/v2/blocks/dialog';
import { BulkCommandArgs, BulkCommandOutcome } from '@workbuddy/ui/v2/blocks/entity.board/context';
import { OutlineEntityKinds, OutlineRow } from '../outline.authoring.model';
import { isOutlineServiceLine } from '../outline.selection';
import { OutlineCrewSelection, OutlineSetCrewDialogComponent } from '../dialogs/outline.set.crew';
import { OutlineRowPatcher } from './outline.bulk.schedule';

export const OUTLINE_SET_CREW_ACTION = SemanticCommand.SetCrew;

export interface OutlineSetCrewDeps {
  /** The entity pair this run is acting on, so one handler serves the live outline and the template one. */
  kinds: OutlineEntityKinds;
  dialogs: WorkBuddyDialogService;
  entityData: EntityDataService;
  store: OutlineRowPatcher;
  toasts: ToastService;
}

export async function handleOutlineSetCrew(args: BulkCommandArgs<OutlineRow>, deps: OutlineSetCrewDeps): Promise<BulkCommandOutcome> {

  if ((args.bulkActionRequest?.actionId ?? args.action) !== OUTLINE_SET_CREW_ACTION) return { handled: false };
  const lines = (args.data ?? []).filter(row => isOutlineServiceLine(row, deps.kinds));
  if (!lines.length) return { handled: true, clearSelection: false };

  // Prefill only for a single line: showing one line's crew while writing to many would read as
  // "these are the current values" when it is really a default for a replace.
  const single = lines.length === 1 ? lines[0] as OutlineRow & { defaultContractor?: Entity; defaultResources?: Entity[] } : undefined;
  // Same width the shared reason prompt opens at — this dialog is deliberately that prompt's twin.
  const selection = await deps.dialogs.modal<OutlineCrewSelection, OutlineSetCrewDialogComponent>(OutlineSetCrewDialogComponent, {
    width: 'min(480px, calc(100vw - 40px))',
    inputs: {
      lineCount: lines.length,
      contractor: single?.defaultContractor ?? null,
      resources: single?.defaultResources ?? [],
    },
    aria: { label: 'Set crew' },
  });
  if (!selection) return { handled: true, clearSelection: false };

  const patch = { defaultContractor: selection.defaultContractor, defaultResources: selection.defaultResources };
  // The outline row's `defaultResources` column renders the merged crew (contractor first), which is
  // what the patch persistence writes back too — so the in-place update must merge, not copy.
  const rowPatch = { defaultResources: outlineCrewEntities(selection.defaultContractor, selection.defaultResources) };
  const patcher = deps.entityData.getPatch(deps.kinds.line);
  let updated = 0;
  let failed = 0;
  for (const line of lines) {
    try {
      await patcher.mutateAsync({ id: line.sourceId, data: patch });
      deps.store.patchDisplayedRow(`${deps.kinds.linePrefix}${line.sourceId}`, rowPatch);
      updated += 1;
    } catch {
      failed += 1;
    }
  }

  if (failed && !updated) deps.toasts.error('Set crew', 'Action failed, please try again.');
  else if (failed) deps.toasts.warning('Set crew', `Updated ${updated} of ${lines.length} lines.`);
  else deps.toasts.success('Set crew', `Updated ${updated} line${updated === 1 ? '' : 's'}.`);
  return { handled: true };
}
